/**
 * Danh sách IP nhập theo dòng, kiểu aaPanel (ADR-0098): mỗi dòng một IP hoặc một dải CIDR, chú thích tuỳ chọn sau
 * dấu `#` ở cuối dòng.
 *
 * ```
 * # Văn phòng
 * 113.185.44.0/24  # Văn phòng Hà Nội
 * 2001:db8::/48    # VPN
 * 203.0.113.7
 * ```
 *
 * Dùng cho hai ô: dải tin cậy (cả danh sách sửa tại chỗ, lưu là thay cả danh sách) và ô thêm vào blocklist (mỗi dòng
 * một lần chặn, chú thích thành lý do).
 *
 * Luật đọc:
 * - cắt khoảng trắng hai đầu; bỏ dòng trống và dòng chỉ có chú thích;
 * - IP trần là một địa chỉ (`/32`, `/128`); dải được đưa về địa chỉ đầu dải như backend (`chuanHoaCidr`);
 * - lỗi theo **số dòng của ô nhập** (đếm cả dòng trống), để người dùng tìm thấy dòng sai;
 * - dòng trùng (sau chuẩn hoá) là lỗi, chỉ ra dòng đầu tiên đã có;
 * - vượt trần số dòng là lỗi ở từng dòng thừa.
 *
 * Hàm thuần: không dựng chuỗi hiển thị, trả mã để view tự dịch. Backend vẫn là chốt chặn.
 */
import { chuanHoaCidr, ipThuocCidr, laTatCaDiaChi } from './cidr.ts';
import { goiYDuoiKhong } from './pham-vi-ip.ts';
import type { DongCidr } from './types.ts';

/** Một dòng hợp lệ đã chuẩn hoá. */
export interface MucIp {
  /** Số dòng trong ô nhập, đếm từ 1. */
  dong: number;
  cidr: string;
  /** Chú thích sau `#`; `null` khi không có. */
  ghiChu: string | null;
}

export type MaLoiDong =
  /** Không phải IP hay CIDR. */
  | 'dinh_dang'
  /** Phần sau `/` sai. */
  | 'tien_to'
  /** Trùng một dòng phía trên (`dongTruoc`). */
  | 'trung'
  /** `0.0.0.0/0`, `::/0`. */
  | 'tat_ca'
  /** Vượt trần số dòng (`toiDa`). */
  | 'qua_so'
  /** Chú thích dài quá `ghiChuToiDa`. */
  | 'ghi_chu_dai'
  /** Dải chứa IP của chính người đang sửa — chặn là tự khoá mình. */
  | 'chua_ip_cua_ban'
  /** Đã có trong danh sách hiện tại (`daCo`). */
  | 'da_co';

export interface LoiDong {
  dong: number;
  ma: MaLoiDong;
  /** Phần IP người dùng gõ, đã cắt khoảng trắng. */
  giaTri: string;
  /** CIDR đã chuẩn hoá, khi đọc được. */
  cidr?: string;
  /** Dòng đã có cùng CIDR (`trung`). */
  dongTruoc?: number;
}

/** Gợi ý không chặn lưu: IP trần đuôi `.0` thường là đầu một dải. */
export interface GoiYDong {
  dong: number;
  ip: string;
  cidr: string;
}

export interface KetQuaDanhSach {
  muc: MucIp[];
  loi: LoiDong[];
  goiY: GoiYDong[];
}

export interface TuyChonDoc {
  /** Trần số dòng hợp lệ. */
  toiDa?: number;
  /** Cấm `/0`. */
  camTatCa?: boolean;
  /** Độ dài tối đa của chú thích. */
  ghiChuToiDa?: number;
  /** IP của người đang sửa: dòng chứa IP này là lỗi `chua_ip_cua_ban`. Để trống thì không kiểm. */
  ipCuaBan?: string | null;
  /** CIDR đã có (đã chuẩn hoá): dòng trùng là lỗi `da_co`. */
  daCo?: readonly string[];
}

/** Tách một dòng thành phần IP và chú thích. IPv6 không có `#`, nên dấu `#` đầu tiên là chú thích. */
function tachDong(dong: string): { ip: string; ghiChu: string | null } {
  const i = dong.indexOf('#');
  if (i < 0) return { ip: dong.trim(), ghiChu: null };
  const ghiChu = dong.slice(i + 1).trim();
  return { ip: dong.slice(0, i).trim(), ghiChu: ghiChu === '' ? null : ghiChu };
}

/** Đọc nội dung ô nhập. */
export function docDanhSachIp(van: string, tc: TuyChonDoc = {}): KetQuaDanhSach {
  const muc: MucIp[] = [];
  const loi: LoiDong[] = [];
  const goiY: GoiYDong[] = [];
  const dongCua = new Map<string, number>();
  const daCo = new Set(tc.daCo ?? []);

  van.split(/\r?\n/).forEach((tho, i) => {
    const dong = i + 1;
    const { ip, ghiChu } = tachDong(tho);
    if (ip === '') return;

    const kq = chuanHoaCidr(ip);
    if (!kq.ok) {
      loi.push({ dong, ma: kq.loi, giaTri: ip });
      return;
    }
    const cidr = kq.cidr;
    const truoc = dongCua.get(cidr);
    if (truoc !== undefined) {
      loi.push({ dong, ma: 'trung', giaTri: ip, cidr, dongTruoc: truoc });
      return;
    }
    dongCua.set(cidr, dong);
    if (tc.camTatCa && laTatCaDiaChi(cidr)) {
      loi.push({ dong, ma: 'tat_ca', giaTri: ip, cidr });
      return;
    }
    if (daCo.has(cidr)) {
      loi.push({ dong, ma: 'da_co', giaTri: ip, cidr });
      return;
    }
    if (tc.ipCuaBan && ipThuocCidr(tc.ipCuaBan, cidr)) {
      loi.push({ dong, ma: 'chua_ip_cua_ban', giaTri: ip, cidr });
      return;
    }
    if (tc.ghiChuToiDa !== undefined && ghiChu !== null && ghiChu.length > tc.ghiChuToiDa) {
      loi.push({ dong, ma: 'ghi_chu_dai', giaTri: ip, cidr });
      return;
    }
    if (tc.toiDa !== undefined && muc.length >= tc.toiDa) {
      loi.push({ dong, ma: 'qua_so', giaTri: ip, cidr });
      return;
    }
    muc.push({ dong, cidr, ghiChu });
    const g = goiYDuoiKhong(ip, 'mot');
    if (g) goiY.push({ dong, ip, cidr: g.cidr });
  });

  return { muc, loi, goiY };
}

/** IP trần cho dòng một địa chỉ (`/32`, `/128`), CIDR cho dải: đúng cách người dùng sẽ gõ. */
export function vietCidr(cidr: string): string {
  const kq = chuanHoaCidr(cidr);
  if (!kq.ok) return cidr;
  const motDiaChi = (kq.phienBan === 4 && kq.tienTo === 32) || (kq.phienBan === 6 && kq.tienTo === 128);
  return motDiaChi ? kq.cidr.slice(0, kq.cidr.indexOf('/')) : kq.cidr;
}

/** Viết danh sách ra nội dung ô nhập (và tệp xuất): mỗi dòng một CIDR, chú thích sau ` # `. */
export function vietDanhSachIp(ds: ReadonlyArray<{ cidr: string; note?: string | null }>): string {
  return ds
    .map((d) => {
      const ghiChu = d.note?.replace(/[\r\n]+/g, ' ').trim();
      return ghiChu ? `${vietCidr(d.cidr)} # ${ghiChu}` : vietCidr(d.cidr);
    })
    .join('\n');
}

/** Dòng hợp lệ thành phần tử `cidr_list` gửi backend. Dải tin cậy không có hạn từng dòng (ADR-0098). */
export function sangDongCidr(muc: readonly MucIp[]): DongCidr[] {
  return muc.map((m) => ({ cidr: m.cidr, note: m.ghiChu ?? '', expires_at: null }));
}

/** Hai danh sách giống nhau theo CIDR, ghi chú và thứ tự (ghi chú rỗng và `null` coi như nhau). */
export function cungDanhSach(
  a: ReadonlyArray<{ cidr: string; note?: string | null }>,
  b: ReadonlyArray<{ cidr: string; note?: string | null }>,
): boolean {
  return (
    a.length === b.length &&
    a.every((x, i) => x.cidr === b[i]!.cidr && (x.note ?? '').trim() === (b[i]!.note ?? '').trim())
  );
}

/**
 * Giá trị của dải tin cậy trong bản nháp: mảng khi ô nhập hợp lệ, **chuỗi** (nguyên văn ô nhập) khi còn dòng sai —
 * để bản nháp sống qua lần đổi thẻ mà dòng sai không mất, và form chặn lưu. Trả danh sách đã đọc được khi giá trị là
 * mảng hoặc chuỗi hợp lệ; `null` khi chuỗi còn dòng sai.
 */
export function danhSachCuaGiaTri(giaTri: unknown, tc: TuyChonDoc = {}): DongCidr[] | null {
  if (Array.isArray(giaTri)) return giaTri as DongCidr[];
  if (typeof giaTri !== 'string') return [];
  const kq = docDanhSachIp(giaTri, tc);
  return kq.loi.length > 0 ? null : sangDongCidr(kq.muc);
}

/**
 * Thêm một IP vào giá trị của dải tin cậy (lối tắt "Thêm vào dải tin cậy" của báo cáo IP ngoài dải). Giữ đúng dạng
 * của bản nháp: mảng thì thêm phần tử, chuỗi thì thêm một dòng ở cuối.
 */
export function themVaoGiaTri(giaTri: unknown, cidr: string, ghiChu: string): unknown {
  if (typeof giaTri === 'string') {
    const dong = `${vietCidr(cidr)} # ${ghiChu}`;
    return giaTri.trim() === '' ? dong : `${giaTri.replace(/\s+$/, '')}\n${dong}`;
  }
  const ds = Array.isArray(giaTri) ? (giaTri as DongCidr[]) : [];
  return [...ds, { cidr, note: ghiChu, expires_at: null }];
}
